import { User } from '@/types/user';
import { FOOTBALL_ROUTE_POLICY } from '@/routes/footballRoutePolicy';

export type NavigationSection = 'home' | 'squad' | 'matches' | 'reports' | 'scouting' | 'insights' | 'admin';

export interface NavigationItem {
  id: string;
  label: string;
  href: string;
  section: NavigationSection;
  roles: User['role'][];
  requiresWyscout?: boolean;
  commandOnly?: boolean;
  mobilePriority?: number;
  keywords: string[];
}

export const SECTION_LABELS: Record<NavigationSection, string> = {
  home: 'Home',
  squad: 'Squad',
  matches: 'Matches & Season',
  reports: 'Reports',
  scouting: 'Scouting',
  insights: 'Insights',
  admin: 'Administration',
};

export const NAVIGATION_ITEMS: NavigationItem[] = [
  { id: 'home', label: 'Home', href: '/dashboard', section: 'home', roles: ['admin', 'manager', 'coach', 'medical'], mobilePriority: 1, keywords: ['dashboard', 'home'] },
  { id: 'calendar', label: 'Calendar', href: '/calendar', section: 'matches', roles: [...FOOTBALL_ROUTE_POLICY.calendar], mobilePriority: 6, keywords: ['calendar', 'schedule'] },
  { id: 'squad', label: 'Squad', href: '/players', section: 'squad', roles: [...FOOTBALL_ROUTE_POLICY.players], mobilePriority: 2, keywords: ['players', 'squad', 'availability'] },
  { id: 'external-players', label: 'External players', href: '/scouting', section: 'scouting', roles: [...FOOTBALL_ROUTE_POLICY.scouting], mobilePriority: 5, keywords: ['external players', 'scouting', 'recruitment'] },
  { id: 'comparison', label: 'Compare players', href: '/comparison', section: 'insights', roles: [...FOOTBALL_ROUTE_POLICY.comparison], mobilePriority: 8, keywords: ['compare', 'comparison', 'players'] },
  { id: 'team', label: 'Team selection', href: '/team', section: 'squad', roles: [...FOOTBALL_ROUTE_POLICY.team], mobilePriority: 7, keywords: ['team', 'selection', 'lineup'] },
  { id: 'matches', label: 'Matches', href: '/matches', section: 'matches', roles: [...FOOTBALL_ROUTE_POLICY.matches], mobilePriority: 3, keywords: ['matches', 'fixtures', 'results'] },
  { id: 'create-match', label: 'Create match', href: '/matches/new', section: 'matches', roles: ['admin', 'manager', 'coach'], commandOnly: true, keywords: ['create', 'new', 'match'] },
  { id: 'season', label: 'Season', href: '/season', section: 'matches', roles: [...FOOTBALL_ROUTE_POLICY.season], requiresWyscout: true, mobilePriority: 9, keywords: ['season', 'overview'] },
  { id: 'external-matches', label: 'League', href: '/league', section: 'matches', roles: [...FOOTBALL_ROUTE_POLICY.league], requiresWyscout: true, mobilePriority: 10, keywords: ['external matches', 'league', 'opposition', 'standings'] },
  { id: 'reports', label: 'Reports', href: '/reports', section: 'reports', roles: [...FOOTBALL_ROUTE_POLICY.reports], mobilePriority: 4, keywords: ['reports', 'performance', 'medical', 'scouting'] },
  { id: 'new-scouting-report', label: 'New scouting report', href: '/reports/scouting/new', section: 'reports', roles: ['admin', 'scout'], commandOnly: true, keywords: ['create', 'new', 'scouting', 'report'] },
  { id: 'new-performance-report', label: 'New performance report', href: '/reports/performance/new', section: 'reports', roles: ['admin', 'coach'], commandOnly: true, keywords: ['create', 'new', 'performance', 'report'] },
  { id: 'new-medical-report', label: 'New medical report', href: '/reports/medical/new', section: 'reports', roles: ['admin', 'medical'], commandOnly: true, keywords: ['create', 'new', 'medical', 'report'] },
  { id: 'new-training-report', label: 'New training report', href: '/reports/training/new', section: 'reports', roles: ['admin', 'coach'], commandOnly: true, keywords: ['create', 'new', 'training', 'report'] },
  { id: 'ai-hub', label: 'AI Hub', href: '/ai-hub', section: 'insights', roles: ['admin', 'manager', 'coach', 'medical'], mobilePriority: 11, keywords: ['ai', 'analysis', 'chat', 'history', 'assistant', 'insights'] },
  { id: 'scheduling', label: 'Scheduling', href: '/admin/scheduling', section: 'admin', roles: ['admin'], mobilePriority: 15, keywords: ['scheduling', 'jobs', 'operations', 'cron'] },
  { id: 'queue', label: 'Queue', href: '/admin/queue', section: 'admin', roles: ['admin'], mobilePriority: 15, keywords: ['queue', 'jobs', 'operations', 'workers'] },
  { id: 'users', label: 'Users', href: '/admin/users', section: 'admin', roles: ['admin'], mobilePriority: 15, keywords: ['users', 'accounts', 'administration'] },
  { id: 'audit-log', label: 'Audit log', href: '/admin/audit-log', section: 'admin', roles: ['admin'], mobilePriority: 16, keywords: ['audit', 'log', 'administration'] },
  { id: 'player-home', label: 'Home', href: '/player', section: 'home', roles: ['player'], mobilePriority: 1, keywords: ['dashboard', 'home'] },
  { id: 'player-medical', label: 'Medical', href: '/player/medical', section: 'squad', roles: ['player'], mobilePriority: 2, keywords: ['medical', 'fitness', 'availability'] },
  { id: 'player-calendar', label: 'Calendar', href: '/player/calendar', section: 'matches', roles: ['player'], mobilePriority: 3, keywords: ['calendar', 'schedule'] },
  { id: 'profile', label: 'Profile', href: '/profile', section: 'home', roles: ['admin', 'manager', 'coach', 'scout', 'medical', 'player'], mobilePriority: 14, keywords: ['profile', 'account', 'password'] },
];

export function getNavigation(role: User['role'], hasWyscout: boolean): NavigationItem[] {
  return NAVIGATION_ITEMS.filter((item) => (
    item.roles.includes(role) && (!item.requiresWyscout || hasWyscout)
  ));
}

export function getPageNavigation(items: NavigationItem[]): NavigationItem[] {
  return items.filter((item) => !item.commandOnly);
}

export function getActiveNavigationItem(
  items: NavigationItem[],
  pathname: string,
): NavigationItem | undefined {
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  return items
    .filter((item) => normalizedPathname === item.href || normalizedPathname.startsWith(`${item.href}/`))
    .sort((left, right) => right.href.length - left.href.length)[0];
}

export function getMobileNavigation(items: NavigationItem[]): NavigationItem[] {
  return getPageNavigation(items)
    .sort((left, right) => (left.mobilePriority ?? Number.MAX_SAFE_INTEGER) - (right.mobilePriority ?? Number.MAX_SAFE_INTEGER))
    .slice(0, 4);
}

export function getBreadcrumbs(
  items: NavigationItem[],
  pathname: string,
  context?: { detailLabel?: string; returnTo?: string },
): Array<{ label: string; href?: string }> {
  pathname = pathname.replace(/\/+$/, '') || '/';
  const activeItem = getActiveNavigationItem(getPageNavigation(items), pathname);
  if (!activeItem) return [];

  const breadcrumbs: Array<{ label: string; href?: string }> = [
    { label: SECTION_LABELS[activeItem.section], href: activeItem.href },
  ];

  if (/^\/(players|scouting)\/[^/]+$/.test(pathname)) {
    const returnTo = context?.returnTo;
    if (returnTo) {
      try {
        const parsed = new URL(returnTo, 'https://local.invalid');
        if (parsed.origin === 'https://local.invalid' && parsed.pathname === activeItem.href) {
          breadcrumbs[0].href = `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
      } catch {}
    }
  }

  if (pathname === activeItem.href) {
    return activeItem.id === 'scheduling' || activeItem.id === 'queue'
      ? [...breadcrumbs, { label: activeItem.label }]
      : breadcrumbs;
  }

  let detailLabel: string;
  if (context?.detailLabel && /^\/(players|scouting)\/[^/]+$/.test(pathname)) {
    detailLabel = context.detailLabel;
  } else if (pathname === '/players/compare') {
    detailLabel = 'Compare players';
  } else if (/^\/(players|scouting)\/[^/]+$/.test(pathname) || /^\/league\/players\/[^/]+$/.test(pathname)) {
    detailLabel = 'Player details';
  } else if (/^\/league\/matches\/[^/]+$/.test(pathname)) {
    detailLabel = 'Match details';
  } else if (pathname === '/league/comparison') {
    detailLabel = 'Player comparison';
  } else if (/^\/(players|scouting)\/new$/.test(pathname)) {
    detailLabel = 'Create player';
  } else if (/^\/(players|scouting)\/[^/]+\/edit$/.test(pathname)) {
    detailLabel = 'Edit player';
  } else if (pathname === '/matches/new') {
    detailLabel = 'Create match';
  } else if (/^\/matches\/[^/]+\/post-match$/.test(pathname)) {
    detailLabel = 'Post-match';
  } else if (/^\/matches\/[^/]+\/edit$/.test(pathname)) {
    detailLabel = 'Edit match';
  } else if (/^\/matches\/(wyscout\/)?[^/]+$/.test(pathname)) {
    detailLabel = 'Match details';
  } else if (/^\/reports\/[^/]+\/new$/.test(pathname)) {
    detailLabel = 'Create report';
  } else if (/^\/reports\/[^/]+\/[^/]+\/edit$/.test(pathname)) {
    detailLabel = 'Edit report';
  } else if (/^\/reports\/[^/]+\/[^/]+$/.test(pathname)) {
    detailLabel = 'Report details';
  } else if (pathname.startsWith('/admin/')) {
    detailLabel = 'Administration details';
  } else {
    detailLabel = `${activeItem.label} details`;
  }

  return [...breadcrumbs, { label: detailLabel }];
}
